import {
  Box,
  Button,
  Checkbox, FormControl, FormControlLabel, FormGroup, MenuItem, Modal, Select, Stack, TextField, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { useCallback } from 'react';
import CloseButton from '../Buttons/Close';
import BlockData from '../../models/BlockData';
import BlockType from '../../models/BlockType';
import { URLOpenType } from '../../models/URLOpenType';
import { URLTargetType } from '../../models/URLTargetType';
import LinkData from '../../models/LinkData';
import ErrorText from '../ErrorText';

interface Props {
  openModal: boolean;
  link: LinkData;
  blockData: BlockData[];
  hideRemoveButton?: boolean;
  errorText?: string;
  onRemoveBlock?: () => void;
  onChangeURLTitle?: (title: string) => void;
  onChangeLink?: (link: string) => void;
  onSelectBlock?: (selected: string) => void;
  onChangeTargetType?: (target: any) => void;
  onChangeOpenType?: (checked: boolean) => void;
  onCloseModal: (event: React.MouseEvent) => void;
  onSaveModal: (event: React.MouseEvent) => void;
}

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '70%',
  bgcolor: 'background.paper',
  border: '1px solid #000',
  boxShadow: 24,
  p: 4,
};

function LinkModal({
  openModal,
  link,
  blockData,
  hideRemoveButton = false,
  errorText,
  onRemoveBlock,
  onChangeURLTitle,
  onChangeLink,
  onSelectBlock,
  onChangeTargetType,
  onChangeOpenType,
  onCloseModal,
  onSaveModal,
}: Props) {
  const blockOptions = blockData.map((block, index) => (
    <MenuItem value={`#${block.id}`} key={block.id}>{`第${index}ブロック：${BlockType.describe(block.type)}`}</MenuItem>
  ));

  const matchBlockId = useCallback(
    (url?: string) => blockData.some(((item) => `#${item.id}` === url)),
    [blockData],
  );

  return (
    <Modal
      open={openModal}
      onClose={onCloseModal}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Stack spacing={2} sx={{ border: '1px dashed lightGray', p: 2, position: 'relative' }}>
          {!hideRemoveButton && <CloseButton updateHandler={onRemoveBlock} />}
          <TextField
            required
            label="テキスト"
            defaultValue={link.title}
            onChange={(event) => onChangeURLTitle(event.target.value)}
            size="small"
          />
          <ToggleButtonGroup
            color="primary"
            value={link.linkData.targetType}
            exclusive
            onChange={(_, newValue) => {
              if (newValue) {
                onChangeTargetType(newValue);
              }
            }}
            size="small"
          >
            <ToggleButton value={URLTargetType.INTERNAL}>ページ内リンク</ToggleButton>
            <ToggleButton value={URLTargetType.EXTERNAL}>直接URLを入力</ToggleButton>
          </ToggleButtonGroup>

          {link.linkData.targetType === URLTargetType.INTERNAL && (
            <>
              <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
                <Select
                  value={matchBlockId(link.linkData.url) ? link.linkData.url : ''}
                  onChange={(event) => onSelectBlock(event.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" key="">
                    <em>選択してください</em>
                  </MenuItem>
                  {blockOptions}
                </Select>
              </FormControl>
            </>
          )}

          {link.linkData.targetType === URLTargetType.EXTERNAL && (
            <>
              <TextField
                required
                label="リンク"
                defaultValue={link.linkData.url}
                onChange={(event) => onChangeLink(event.target.value)}
                size="small"
              />
              <FormGroup>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={link.linkData.openType === URLOpenType.NEW_TAB}
                      onChange={(event) => onChangeOpenType(event.target.checked)}
                    />
                  )}
                  label="別タブで開く"
                />
              </FormGroup>
            </>
          )}

        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          mt={2}
        >
          <Button variant="outlined" color="primary" onClick={onCloseModal}>
            キャンセル
          </Button>

          {errorText ? <ErrorText text={errorText} /> : null}

          <Button variant="contained" color="primary" onClick={onSaveModal}>
            適用
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
}

export default LinkModal;
